import I18n from 'I18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEffectEvent } from 'academy-common-lib/hooks/useEffectEvent';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { AISearchInput } from 'ai-components-ui-library/components/inputs/AISearchInput';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { H2 } from 'foundations-components/transitional/components/H2';
import { useConversationList } from '../../context/ConversationListContext';
import { useConversationMutations } from '../../hooks/useConversationMutations';
import { useConversationSearch } from '../../hooks/useConversationSearch';
import { useDrawerPersistence } from '../../hooks/useDrawerPersistence';
import { groupByTimeBucket } from '../../utils/conversationGrouping';
import { ConversationGroup } from './ConversationGroup';
import { ConversationRow } from './ConversationRow';
import { DrawerEmptyState } from './DrawerEmptyState';
import { DrawerErrorState } from './DrawerErrorState';
import { DrawerSkeletonRows } from './DrawerSkeletonRows';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ConversationHistoryDrawer = ({
  activeConversationId,
  onOpenConversation,
  onStartNew,
  trackEvent,
  defaultOpen,
  onOpenChange
}) => {
  const {
    open,
    setOpen
  } = useDrawerPersistence();
  const {
    items,
    hasMore,
    loading,
    initialLoading,
    error,
    refetch,
    loadMore,
    patch,
    remove,
    restore
  } = useConversationList();
  const {
    renameConversation,
    deleteConversation
  } = useConversationMutations({
    patch,
    remove,
    restore,
    activeConversationId,
    onActiveDeleted: () => {
      onStartNew();
    }
  });
  const {
    isMobileNavbar
  } = useMediaQuery();
  const maybeOpenOnMount = useEffectEvent(() => {
    if (isMobileNavbar) {
      setOpen(!!defaultOpen);
    }
  });
  useEffect(() => {
    maybeOpenOnMount();
  }, []);
  useEffect(() => {
    onOpenChange === null || onOpenChange === void 0 || onOpenChange(open);
  }, [open, onOpenChange]);
  const [sentinelNode, setSentinelNode] = useState(null);
  const sentinelRef = useCallback(node => setSentinelNode(node), []);
  useEffect(() => {
    if (!sentinelNode || !hasMore || loading) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        loadMore();
      }
    });
    observer.observe(sentinelNode);
    return () => observer.disconnect();
  }, [sentinelNode, hasMore, loading, loadMore]);
  const {
    searchTerm,
    onSearchChange,
    filteredItems,
    hasSearchTerm,
    isEmptyResult
  } = useConversationSearch(items);
  const grouped = useMemo(() => groupByTimeBucket(filteredItems), [filteredItems]);
  const renderList = closeAfterAction => {
    const wrapOpen = cb => closeAfterAction ? id => {
      cb(id);
      setOpen(false);
    } : cb;
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      flex: 1,
      overflow: "hidden",
      children: [!initialLoading && !error && items.length > 0 && /*#__PURE__*/_jsx(View, {
        paddingInline: "500",
        paddingBlockEnd: "100",
        paddingBlockStart: "100",
        children: /*#__PURE__*/_jsx(AISearchInput, {
          "data-test-id": "drawer-search-input",
          value: searchTerm,
          onChange: onSearchChange,
          placeholder: I18n.text('academy-ai-assistant.drawer.searchPlaceholder'),
          borderColor: "border-core-default",
          shrink: true
        })
      }), /*#__PURE__*/_jsx(View, {
        role: "listbox",
        "aria-label": I18n.text('academy-ai-assistant.drawer.title'),
        flex: 1,
        overflow: "auto",
        paddingInline: "300",
        children: initialLoading ? /*#__PURE__*/_jsx(DrawerSkeletonRows, {}) : error ? /*#__PURE__*/_jsx(DrawerErrorState, {
          onRetry: refetch
        }) : items.length === 0 ? /*#__PURE__*/_jsx(DrawerEmptyState, {}) : isEmptyResult ? /*#__PURE__*/_jsx(Text, {
          color: "text-primary-disabled",
          paddingBlock: "400",
          textAlign: "center",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academy-ai-assistant.drawer.noSearchResults"
          })
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [grouped.map(group => /*#__PURE__*/_jsx(ConversationGroup, {
            bucket: group.bucket,
            children: group.rows.map(row => /*#__PURE__*/_jsx(ConversationRow, {
              row: row,
              isActive: row.conversationId === activeConversationId,
              isMobile: isMobileNavbar,
              onOpen: wrapOpen(id => onOpenConversation(id)),
              onRename: (r, newTitle) => renameConversation(r, newTitle),
              onDelete: r => {
                const idx = items.findIndex(x => x.conversationId === r.conversationId);
                deleteConversation(r, idx).catch(Raven.captureException.bind(Raven));
              },
              trackEvent: trackEvent
            }, row.conversationId))
          }, group.bucket)), hasMore && !hasSearchTerm ? /*#__PURE__*/_jsx(View, {
            ref: sentinelRef,
            "data-test-id": "drawer-pagination-sentinel"
          }) : null]
        })
      })]
    });
  };
  if (isMobileNavbar) {
    if (!open) return null;
    return /*#__PURE__*/_jsxs(Modal, {
      use: "default",
      size: "auto",
      width: "100%",
      "data-test-id": "conversation-history-drawer",
      onEsc: () => setOpen(false),
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academy-ai-assistant.drawer.title"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: () => setOpen(false)
        })]
      }), /*#__PURE__*/_jsx(DialogBody, {
        children: renderList(true)
      })]
    });
  }
  return /*#__PURE__*/_jsx(View, {
    as: "aside",
    "data-test-id": "conversation-history-drawer",
    inlineSize: "350px",
    blockSize: "100%",
    backgroundColor: "fill-surface-default",
    borderInlineEndWidth: "100",
    borderInlineEndStyle: "solid",
    borderColor: "border-core-subtle",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      blockSize: "100%",
      children: [/*#__PURE__*/_jsx(View, {
        paddingBlockStart: "600",
        paddingBlockEnd: "200",
        paddingInline: "500",
        children: /*#__PURE__*/_jsx(Text, {
          typography: "heading-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academy-ai-assistant.drawer.title"
          })
        })
      }), renderList(false)]
    })
  });
};